import { supabase, Event, Registration, User, Minor } from '../lib/supabase'

// Dashboard statistics interfaces
export interface PendingPayment {
  id: string
  eventId: string
  eventTitle: string
  registrationId: string
  amount: number
  date: string
  status: 'pending' | 'completed' | 'failed'
  currency: string
  paymentReference: string
}

export interface UpcomingRegisteredEvent {
  id: string
  title: string
  date: string
  location: string
  image_url?: string
  difficulty?: string
  current_participants?: number
  max_participants?: number
  /** True when every registration for this event is paid */
  paid: boolean
  /** Number of participants this user registered for the event */
  registrationCount: number
}

export interface UserDashboardStats {
  upcomingEvents: UpcomingRegisteredEvent[]
  userRegistrations: Registration[]
  userMinors: Minor[]
  totalRegistrations: number
  upcomingRegistrations: number
  pendingPayments: PendingPayment[]
}

export interface CoordinatorDashboardStats {
  activeEvents: number
  totalParticipants: number
  upcomingEvents: number
  registrationRate: number
  managedEvents: Event[]
  recentRegistrations: Registration[]
}

export interface AdminDashboardStats {
  totalUsers: number
  totalEvents: number
  totalRegistrations: number
  registeredMinors: number
  coordinators: number
  recentActivity: any[]
  systemHealth: {
    eventsThisMonth: number
    registrationsThisMonth: number
    activeUsers: number
  }
}

export interface DashboardUser {
  id: string
  name: string
  email: string
  profile_image_url?: string
  last_login?: string
  role: 'user' | 'coordinator' | 'admin'
}

// Dashboard service
export const dashboardService = {
  // Get current user with role determination
  async getCurrentDashboardUser(): Promise<DashboardUser> {
    try {
      // Check if there's a logged-in user from localStorage
      const currentUserId = localStorage.getItem('gomtb_current_user_id');
      const currentUserEmail = localStorage.getItem('gomtb_current_user_email');
      
      if (currentUserId && currentUserEmail) {
        // Load the actual user from database - be flexible about which fields exist
        const { data: user, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', currentUserId)
          .single();
          
        if (user && !error) {
          // Get user role from database
          const role = await this.determineUserRole(user.email);
          
          return {
            id: user.id,
            name: user.name || user.full_name || 'User', // Handle different name field possibilities
            email: user.email,
            profile_image_url: user.profile_image_url || user.avatar_url || undefined,
            last_login: new Date().toISOString(), // Current login time
            role: role
          };
        }
      }
      
      // If no logged-in user, return null - user must be authenticated
      throw new Error('User must be logged in to access dashboard');
    } catch (error) {
      console.error('Error fetching dashboard user:', error);
      // Re-throw error - no guest access in production
      throw error;
    }
  },

  // Determine user role based on database lookup
  async determineUserRole(email: string): Promise<'user' | 'coordinator' | 'admin'> {
    try {
      // Check coordinator_profiles table for user role
      const { data: profile, error } = await supabase
        .from('coordinator_profiles')
        .select('role, status')
        .eq('user_email', email)
        .eq('status', 'active')
        .single();

      if (profile && !error) {
        return profile.role;
      }
      
      return 'user'; // Default to user if not found in coordinator_profiles
    } catch (error) {
      console.error('Error determining user role:', error);
      return 'user';
    }
  },

  // Get user dashboard statistics
  async getUserDashboardStats(userId: string): Promise<UserDashboardStats> {
    try {
      // Get the current user first to get their email
      const currentUser = await this.getCurrentDashboardUser()
      
      // User must be authenticated to access dashboard stats
      if (!currentUser || !currentUser.email) {
        throw new Error('User must be logged in to access dashboard stats');
      }
      
      // For real users, get their actual data
      // Get user's registrations using the actual user's email
      const { data: userRegistrations } = await supabase
        .from('registrations')
        .select(`
          *,
          events (
            id,
            title,
            date,
            location,
            image_url,
            difficulty,
            current_participants,
            max_participants
          )
        `)
        .eq('user_email', currentUser.email)
        .order('registration_date', { ascending: false })

      // All upcoming events the user registered for (paid or not), deduped
      // per event with an aggregated paid/unpaid status
      const upcomingEventsMap = new Map<string, UpcomingRegisteredEvent>()
      userRegistrations?.forEach(reg => {
        const event = reg.events
        if (!event || new Date(event.date) <= new Date()) return
        if (reg.status === 'cancelled') return

        const isPaid = reg.payment_status === 'completed'
        const existing = upcomingEventsMap.get(event.id)

        if (existing) {
          existing.registrationCount += 1
          existing.paid = existing.paid && isPaid
        } else {
          upcomingEventsMap.set(event.id, {
            id: event.id,
            title: event.title,
            date: event.date,
            location: event.location,
            image_url: event.image_url,
            difficulty: event.difficulty,
            current_participants: event.current_participants,
            max_participants: event.max_participants,
            paid: isPaid,
            registrationCount: 1,
          })
        }
      })
      const upcomingUserEvents = Array.from(upcomingEventsMap.values())
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

      // Get user's minors
      const { data: userMinors } = await supabase
        .from('minors')
        .select('*')
        .eq('user_id', userId)
        .order('name', { ascending: true })

      // Get pending and failed payments from payment_transactions
      const { data: pendingPayments } = await supabase
        .from('payment_transactions')
        .select(`
          id,
          event_id,
          registration_id,
          amount,
          status,
          currency,
          events (
            id,
            title,
            date
          )
        `)
        .eq('user_id', userId)
        .in('status', ['pending', 'failed'])
        .order('created_at', { ascending: false });

      // Format pending payments
      const formattedPendingPayments = pendingPayments?.map(payment => {
        // The events property from the query is an object, not an array
        const eventData = payment.events as unknown as { id: string; title: string; date: string };
        return {
          id: payment.id,
          eventId: payment.event_id,
          eventTitle: eventData?.title || 'Unknown Event',
          registrationId: payment.registration_id,
          amount: payment.amount,
          date: eventData?.date || new Date().toISOString(),
          status: payment.status,
          currency: payment.currency || 'ZAR',
          paymentReference: `PAY-${payment.id.substring(0, 8).toUpperCase()}`
        };
      }) || [];

      // Calculate statistics
      const totalRegistrations = userRegistrations?.length || 0
      const upcomingRegistrations = userRegistrations?.filter(reg => 
        new Date(reg.events?.date || '') > new Date()
      ).length || 0

      return {
        upcomingEvents: upcomingUserEvents, // All events user registered for, with paid status
        userRegistrations: userRegistrations || [],
        userMinors: userMinors || [],
        totalRegistrations,
        upcomingRegistrations,
        pendingPayments: formattedPendingPayments
      }
    } catch (error) {
      console.error('Error fetching user dashboard stats:', error)
      return {
        upcomingEvents: [],
        userRegistrations: [],
        userMinors: [],
        totalRegistrations: 0,
        upcomingRegistrations: 0,
        pendingPayments: []
      }
    }
  },

  // Get coordinator dashboard statistics
  async getCoordinatorDashboardStats(): Promise<CoordinatorDashboardStats> {
    try {
      // Get current user to verify coordinator access
      const currentUser = await this.getCurrentDashboardUser();
      
      // Check if there are any events managed by this coordinator
      const { data: allEvents } = await supabase
        .from('events')
        .select('*')
        .eq('organizer_id', currentUser.id)
        .order('date', { ascending: true })

      // This code would run for real authenticated coordinators
      // Get all registrations for statistics
      const { data: allRegistrations } = await supabase
        .from('registrations')
        .select('*')

      // Calculate statistics
      const now = new Date()
      const activeEvents = allEvents?.filter(event => 
        new Date(event.date) > now
      ).length || 0

      const upcomingEvents = allEvents?.filter(event => {
        const eventDate = new Date(event.date)
        const thirtyDaysFromNow = new Date()
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)
        return eventDate > now && eventDate <= thirtyDaysFromNow
      }).length || 0

      const totalParticipants = allRegistrations?.length || 0

      // Calculate registration rate (registered vs capacity)
      const totalCapacity = allEvents?.reduce((sum, event) => sum + (event.max_participants || 0), 0) || 1
      const registrationRate = Math.round((totalParticipants / totalCapacity) * 100)

      // Get recent registrations
      const { data: recentRegistrations } = await supabase
        .from('registrations')
        .select(`
          *,
          events (
            title,
            date
          )
        `)
        .order('registration_date', { ascending: false })
        .limit(10)

      return {
        activeEvents,
        totalParticipants,
        upcomingEvents,
        registrationRate: Math.min(registrationRate, 100),
        managedEvents: allEvents?.slice(0, 5) || [],
        recentRegistrations: recentRegistrations || []
      }
    } catch (error) {
      console.error('Error fetching coordinator dashboard stats:', error)
      return {
        activeEvents: 0,
        totalParticipants: 0,
        upcomingEvents: 0,
        registrationRate: 0,
        managedEvents: [],
        recentRegistrations: []
      }
    }
  },

  // Get admin dashboard statistics
  async getAdminDashboardStats(): Promise<AdminDashboardStats> {
    try {
      // Get total counts
      const [
        { count: totalUsers },
        { count: totalEvents },
        { count: totalRegistrations },
        { count: registeredMinors }
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('events').select('*', { count: 'exact', head: true }),
        supabase.from('registrations').select('*', { count: 'exact', head: true }),
        supabase.from('minors').select('*', { count: 'exact', head: true })
      ])

      // Get coordinators count (users with coordinator role)
      const { count: coordinators } = await supabase
        .from('coordinator_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')

      // Get this month's statistics
      const startOfMonth = new Date()
      startOfMonth.setDate(1)
      startOfMonth.setHours(0, 0, 0, 0)

      const { count: eventsThisMonth } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString())

      const { count: registrationsThisMonth } = await supabase
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .gte('registration_date', startOfMonth.toISOString())

      // Get recent activity from database
      const { data: recentRegistrations } = await supabase
        .from('registrations')
        .select(`
          id,
          registration_date,
          user_email,
          events (title)
        `)
        .order('registration_date', { ascending: false })
        .limit(5)

      const { data: recentEvents } = await supabase
        .from('events')
        .select(`
          id,
          title,
          created_at,
          organizer_email
        `)
        .order('created_at', { ascending: false })
        .limit(5)

      const recentActivity = [
        ...(recentRegistrations?.map(reg => ({
          type: 'registration',
          description: `New registration for ${(reg.events as any)?.title || 'event'}`,
          timestamp: reg.registration_date,
          user: reg.user_email
        })) || []),
        ...(recentEvents?.map(event => ({
          type: 'event',
          description: `New event created: ${event.title}`,
          timestamp: event.created_at,
          user: event.organizer_email || 'Event Coordinator'
        })) || [])
      ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 10)

      return {
        totalUsers: totalUsers || 0,
        totalEvents: totalEvents || 0,
        totalRegistrations: totalRegistrations || 0,
        registeredMinors: registeredMinors || 0,
        coordinators: coordinators || 0,
        recentActivity,
        systemHealth: {
          eventsThisMonth: eventsThisMonth || 0,
          registrationsThisMonth: registrationsThisMonth || 0,
          activeUsers: totalUsers || 0 // All registered users are considered active
        }
      }
    } catch (error) {
      console.error('Error fetching admin dashboard stats:', error)
      return {
        totalUsers: 0,
        totalEvents: 0,
        totalRegistrations: 0,
        registeredMinors: 0,
        coordinators: 0,
        recentActivity: [],
        systemHealth: {
          eventsThisMonth: 0,
          registrationsThisMonth: 0,
          activeUsers: 0
        }
      }
    }
  },

  // Get notifications for user
  async getUserNotifications(userId: string): Promise<any[]> {
    try {
      // Query real notifications from database
      const { data: notifications, error } = await supabase
        .from('user_notifications')
        .select(`
          *,
          notifications (
            id,
            title,
            message,
            type,
            priority,
            category,
            created_at,
            action_url,
            action_text
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) {
        console.error('Error fetching user notifications:', error)
        return []
      }

      return notifications || []
    } catch (error) {
      console.error('Error in getUserNotifications:', error)
      return []
    }
  },

  // Check if user has outstanding payments
  async hasOutstandingPayments(userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('payment_transactions')
      .select('id')
      .eq('user_id', userId)
      .in('status', ['pending', 'failed']);
    if (error) {
      console.error('Error checking outstanding payments:', error);
      return false;
    }
    return !!(data && data.length > 0);
  }
} 